import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { queries } from '../../keys';
import { useAuthStore } from '~/store/auth';

export interface AddToFavoritesRequest {
  complex_id: string;
}

export const useAddToFavorites = () => {
  const queryClient = useQueryClient();
  const authStore = useAuthStore();

  return useMutation({
    mutationFn: async (data: AddToFavoritesRequest) => {
      return await $fetch('/api/favorites', {
        method: 'POST',
        body: { complex_id: data.complex_id },
        headers: {
          Authorization: `Bearer ${authStore.token}`,
        },
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queries.favorites.list.queryKey });
      queryClient.invalidateQueries({ queryKey: queries.favorites.isFavorited(variables.complex_id).queryKey });
      queryClient.invalidateQueries({ queryKey: ['properties', 'favorites'] });
    },
  });
};

export const useRemoveFromFavorites = () => {
  const queryClient = useQueryClient();
  const authStore = useAuthStore();

  return useMutation({
    mutationFn: async ({ complexId }: { complexId: string; userId: string }) => {
      return await $fetch(`/api/favorites/${complexId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${authStore.token}`,
        },
      });
    },
    onSuccess: (_, { complexId }) => {
      queryClient.invalidateQueries({ queryKey: queries.favorites.list.queryKey });
      queryClient.invalidateQueries({ queryKey: queries.favorites.isFavorited(complexId).queryKey });
      queryClient.invalidateQueries({ queryKey: ['properties', 'favorites'] });
    },
  });
};

export const useIsPropertyFavorited = (complexId: string, userId: string) => {
  const authStore = useAuthStore();

  return useQuery({
    ...queries.favorites.isFavorited(complexId),
    queryFn: () =>
      $fetch(`/api/favorites/${complexId}/status`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authStore.token}`,
        },
      }),
    select: (data) => data.data.is_favorited,
    enabled: false,
  });
};

export const useBatchFavoriteStatus = (complexIds: string[], userId: string) => {
  const authStore = useAuthStore();

  return useQuery({
    queryKey: ['favorites', 'batch-status', complexIds.sort().join(','), userId],
    queryFn: () =>
      $fetch('/api/favorites/batch-status', {
        method: 'POST',
        body: { complex_ids: complexIds },
        headers: {
          Authorization: `Bearer ${authStore.token}`,
        },
      }),
    select: (data) => data.data.favorited_ids as string[],
    enabled: complexIds.length > 0 && !!userId,
  });
};
